import { useRef, type Ref } from "react";
import {
  ArrowDownTrayIcon,
  PencilSquareIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";
import type { Company, SaveStatus, View } from "../types.ts";

type Props = {
  version: number;
  status: SaveStatus;
  canSave: boolean;
  view: View;
  onViewChange: (view: View) => void;
  onAdd: () => void;
  onSave: () => void;
  onImportFile: (file: File) => void;
  onImportFixedCosts: () => void;
  companies: Company[];
  companyId: string;
  onCompanyChange: (companyId: string) => void;
  onManageCompanies: () => void;
  manageCompaniesRef: Ref<HTMLButtonElement>;
};

function statusText(version: number, status: SaveStatus): string {
  let s = `v${version}`;
  if (status === "saving") s += " • sparar…";
  else if (status === "invalid") s += " • ogiltigt datum";
  else if (status === "dirty") s += " • osparat";
  return s;
}

const TABS: Array<{ value: View; label: string }> = [
  { value: "likviditet", label: "Likviditet" },
  { value: "kostnader", label: "Kostnader" },
];

const ADD_LABEL: Record<View, string> = {
  likviditet: "+ Ny rad",
  kostnader: "+ Ny kostnad",
};

export function Header({
  version,
  status,
  canSave,
  view,
  onViewChange,
  onAdd,
  onSave,
  onImportFile,
  onImportFixedCosts,
  companies,
  companyId,
  onCompanyChange,
  onManageCompanies,
  manageCompaniesRef,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <header>
      <h1>Likvid</h1>
      <div className="company-picker">
        <div className="select-wrap">
          <select
            aria-label="Företag"
            value={companyId}
            onChange={(e) => onCompanyChange(e.currentTarget.value)}
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="select-chevron" aria-hidden="true" />
        </div>
        <button
          ref={manageCompaniesRef}
          type="button"
          className="icon-btn"
          title="Hantera företag"
          aria-label="Hantera företag"
          onClick={onManageCompanies}
        >
          <PencilSquareIcon aria-hidden="true" />
        </button>
      </div>
      <nav className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={view === t.value}
            className={`tab${view === t.value ? " active" : ""}`}
            onClick={() => onViewChange(t.value)}
          >
            {t.label}
          </button>
        ))}
      </nav>
      <span className="meta" data-status={status}>
        {statusText(version, status)}
      </span>
      <div className="actions">
        {view === "likviditet" && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              data-testid="import-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) onImportFile(file);
              }}
            />
            <button
              className="btn-with-icon"
              onClick={() => fileRef.current?.click()}
            >
              <ArrowDownTrayIcon aria-hidden="true" />
              Importera CSV
            </button>
          </>
        )}
        {view === "kostnader" && (
          <button className="btn-with-icon" onClick={onImportFixedCosts}>
            <CalendarDaysIcon aria-hidden="true" />
            Månadstransport
          </button>
        )}
        <button onClick={onAdd}>{ADD_LABEL[view]}</button>
        <button className="primary" disabled={!canSave} onClick={onSave}>
          Spara
        </button>
      </div>
    </header>
  );
}
